/**
 * Meta Ads (fase 1): flags del Front, códigos de error y mensajes.
 * Contrato: argenlotapi `docs/frontend/FRONTEND_META_ADS.md`. Ver docs/maps/meta-ads.md.
 *
 * Los errores se deciden siempre por `code`, nunca por el texto de `error`.
 */

const isTrue = (raw) => String(raw ?? '').trim().toLowerCase() === 'true';

/**
 * ¿Sección Meta Ads prendida en este build? Solo con `REACT_APP_META_ADS_ENABLED=true`
 * (apagada por defecto). Además, la sección se oculta si `GET /getmetaconnection`
 * da 404 sin `code` (flag `META_ADS_ENABLED` apagado en la API).
 *
 * @param {string} [raw] Valor de la variable (para tests).
 * @returns {boolean}
 */
export const isMetaAdsEnabled = (raw = process.env.REACT_APP_META_ADS_ENABLED) => isTrue(raw);

const LOCAL_HOSTS = ['localhost', '127.0.0.1'];

/**
 * Solo para probar en localhost (`.env.local`): `REACT_APP_META_DEMO_DISCONNECTED=true`
 * hace de cuenta que el dueño desconectó Meta **sin** borrar datos (E6 no tiene UI
 * todavía). Pisa la respuesta de `getmetaconnection` como la deja `disconnectmeta`
 * sin `purge_data` en DEV real: `not_connected`, sin token, con `last_sync_at` y los
 * contadores en 0 (Ajuste A). El resto de las rutas no se tocan (la API igual conserva el historial).
 * Fuera de localhost, o si la conexión ya no estaba activa, no hace nada.
 *
 * @param {object} data `data` de `GET /getmetaconnection`.
 * @param {{ raw?: string, hostname?: string }} [options]
 * @returns {object}
 */
export function simulateMetaDisconnected(data, {
  raw = process.env.REACT_APP_META_DEMO_DISCONNECTED,
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
} = {}) {
  if (!isTrue(raw) || !LOCAL_HOSTS.includes(hostname) || !data || typeof data !== 'object') return data;
  if (data.status !== 'active' && data.status !== 'expired' && data.connected !== true) return data;
  return {
    ...data,
    status: 'not_connected',
    connected: false,
    needs_reconnect: false,
    connected_at: null,
    token_expires_at: null,
    token_expiring_soon: false,
    sync_running: false,
    scopes: [],
    ad_accounts: { available_count: 0, tracked_count: 0, initial_load_pending: false, initial_load_pending_count: 0 },
    ads: { relevant_count: 0, unlinked_count: 0, unlinked_new_count: 0 },
    checklist: (Array.isArray(data.checklist) ? data.checklist : [])
      .map((step) => (step?.key === 'connect' ? { ...step, status: 'pending' } : step)),
  };
}

/** `code` del modo demo si la API no manda `fixture_code`. */
export const META_FIXTURE_CODE = 'fixture-code';

/** Ruta (HashRouter) que abre el panel de Meta Ads. La usa la vuelta del callback. */
export const META_ADS_PANEL_PATH = '/meta-ads';
/** Ruta del callback de OAuth (`META_OAUTH_REDIRECT_URI` = `<origin>/meta-callback`). */
export const META_CALLBACK_PATH = '/meta-callback';

/** Hosts donde la API ya usa su redirect por defecto: ahí no se manda `redirect_uri`. */
const META_DEFAULT_REDIRECT_HOSTS = ['dev-app.terrio.net', 'app.terrio.net'];

/**
 * `redirect_uri` para `GET /getmetaauthurl`: solo fuera del dominio por defecto
 * del entorno (p. ej. `http://localhost:3000/meta-callback`). En dev-app / app
 * no se manda y la API usa la primera de su lista.
 *
 * @param {{ origin: string, hostname: string }} [loc]
 * @returns {string|null}
 */
export function getMetaRedirectUriParam(loc = window.location) {
  if (!loc?.origin || META_DEFAULT_REDIRECT_HOSTS.includes(loc.hostname)) return null;
  return `${loc.origin}${META_CALLBACK_PATH}`;
}

const parseUrl = (value) => {
  try {
    return new URL(String(value || ''));
  } catch {
    return null;
  }
};

const isHttpsOrLocalhost = (url) => url.protocol === 'https:'
  || (url.protocol === 'http:' && (url.hostname === 'localhost' || url.hostname === '127.0.0.1'));

/** Login real: solo https de facebook.com (defensa contra un redirect abierto). */
const META_AUTH_HOST_RE = /(^|\.)facebook\.com$/i;

/**
 * ¿`auth_url` de modo live es https de facebook.com?
 *
 * @param {string} url
 * @returns {boolean}
 */
export function isSafeMetaLiveAuthUrl(url) {
  const parsed = parseUrl(url);
  return Boolean(parsed && parsed.protocol === 'https:' && META_AUTH_HOST_RE.test(parsed.hostname));
}

/**
 * ¿`auth_url` de modo demo vuelve a la `redirect_uri` que firmó la API?
 * (Mismo origen y path; https, o http solo en localhost.)
 *
 * @param {string} url
 * @param {string} redirectUri
 * @returns {boolean}
 */
export function isSafeMetaFixtureAuthUrl(url, redirectUri) {
  const parsed = parseUrl(url);
  const target = parseUrl(redirectUri);
  if (!parsed || !target || !isHttpsOrLocalhost(parsed)) return false;
  return parsed.origin === target.origin && parsed.pathname === target.pathname;
}

/**
 * Qué hace «Conectar con Meta» con la respuesta de `GET /getmetaauthurl`:
 * - `mode: "live"` (o sin `mode`): redirigir a `auth_url` (https de facebook.com).
 *   El flag de demo **no** gana sobre live.
 * - `mode: "fixture"`: POST directo `{ code: fixture_code, state }` si
 *   `REACT_APP_META_FIXTURE_CONNECT=true` (localhost), si `auth_url` es `null`,
 *   o si `auth_url` vuelve a otro origen (no sacar al usuario de este Terrio).
 *   Si no, redirigir a `auth_url` (ejercita el callback real, p. ej. en dev-app).
 *
 * @param {object} data `data` de `GET /getmetaauthurl`.
 * @param {{ origin?: string, raw?: string }} [options]
 * @returns {{ kind: 'redirect', url: string } | { kind: 'post', code: string, state: string } | { kind: 'invalid' }}
 */
export function resolveMetaConnectAction(data, {
  origin = window.location.origin,
  raw = process.env.REACT_APP_META_FIXTURE_CONNECT,
} = {}) {
  const authUrl = data?.auth_url || null;
  const mode = String(data?.mode || 'live').trim().toLowerCase();
  if (mode !== 'fixture') {
    return isSafeMetaLiveAuthUrl(authUrl) ? { kind: 'redirect', url: authUrl } : { kind: 'invalid' };
  }
  const state = String(data?.state || '').trim();
  if (!state) return { kind: 'invalid' };
  const post = { kind: 'post', code: String(data?.fixture_code || META_FIXTURE_CODE), state };
  if (isTrue(raw) || !authUrl) return post;
  if (!isSafeMetaFixtureAuthUrl(authUrl, data?.redirect_uri)) return { kind: 'invalid' };
  return parseUrl(authUrl).origin === origin ? { kind: 'redirect', url: authUrl } : post;
}

/**
 * `code` estable de un error de la API, en mayúsculas. Sin `code` → ''.
 *
 * @param {object} res Envelope de `httpClient`.
 * @returns {string}
 */
export function readMetaErrorCode(res) {
  const code = res?.code ?? res?.data?.code;
  return typeof code === 'string' ? code.trim().toUpperCase() : '';
}

/**
 * 404 sin `code` = la ruta no existe para el router (flag de la API apagado).
 * Un 404 de negocio (`META_AD_NOT_FOUND`, `RECORD_NOT_FOUND`…) siempre trae `code`.
 *
 * @param {object} res Envelope.
 * @returns {boolean}
 */
export function isMetaAdsOffResponse(res) {
  return res?._httpStatus === 404 && !readMetaErrorCode(res);
}

export const META_GENERIC_ERROR_MESSAGE = 'No pudimos completar la acción. Probá de nuevo en un rato.';
export const META_NETWORK_MESSAGE = 'No se pudo hablar con el servidor.';
export const META_OWNER_ONLY_MESSAGE = 'Solo el dueño del espacio puede conectar o cambiar la cuenta de Meta.';
export const META_CONNECT_CANCELLED_MESSAGE = 'No se conectó. Podés intentarlo de nuevo.';
export const META_SYNC_TIME_BUDGET_MESSAGE = 'Trajimos una parte; el resto se completa en la próxima actualización.';
export const META_REDIRECT_URI_MESSAGE = 'Esta dirección de Terrio no está habilitada para volver de Meta.';
export const META_SESSION_EXPIRED_MESSAGE = 'Tu sesión venció. Iniciá sesión de nuevo.';

/** Mensaje al usuario por `code` (FRONTEND_META_ADS.md § Códigos de error). */
const META_ERROR_MESSAGES = {
  META_STATE_INVALID: 'La conexión venció. Probá de nuevo.',
  META_OAUTH_FAILED: 'Meta no aceptó la conexión. Probá de nuevo.',
  AUTH_REQUIRED: META_SESSION_EXPIRED_MESSAGE,
  META_OWNER_ONLY: META_OWNER_ONLY_MESSAGE,
  SPACE_FORBIDDEN: 'No tenés acceso a los anuncios de este espacio.',
  META_AD_NOT_FOUND: 'Ese anuncio ya no está en tu cuenta. Actualizamos la lista.',
  META_CAMPAIGN_NOT_FOUND: 'Esa campaña ya no está en tu cuenta. Actualizamos la lista.',
  META_AD_ACCOUNT_NOT_FOUND: 'Esa cuenta publicitaria ya no está disponible. Actualizamos la lista.',
  RECORD_NOT_FOUND: 'Esa unidad ya no existe o no es de este espacio.',
  META_NOT_CONNECTED: 'Meta no está conectado.',
  META_TOKEN_EXPIRED: 'Se cortó la conexión con Meta. Tus datos anteriores siguen acá.',
  META_PERMISSION_MISSING: 'Falta un permiso de Meta. Sin eso no podemos leer tus campañas.',
  META_CAMPAIGN_NO_ADS: 'La campaña todavía no tiene anuncios. Actualizá ahora y probá de nuevo.',
  META_SYNC_IN_PROGRESS: 'Ya estamos actualizando los datos. Esperá un momento.',
  META_SYNC_THROTTLED: 'Los datos se pueden actualizar una vez por hora.',
  META_SYNC_TIME_BUDGET: META_SYNC_TIME_BUDGET_MESSAGE,
  META_RATE_LIMITED: 'Meta nos pidió esperar un momento. Probá de nuevo en unos minutos.',
  META_API_ERROR: 'Meta respondió con un error. No se cambió nada.',
  META_UNAVAILABLE: 'Meta no respondió. Probá de nuevo más tarde.',
  META_NOT_CONFIGURED: 'Campañas no está configurado en este entorno. Avisale a soporte.',
  META_ADS_NOT_MIGRATED: 'Campañas no está configurado en este entorno. Avisale a soporte.',
  UNIT_REPORT_SHARE_LIMIT: 'Esta propiedad ya tiene 10 links activos. Se liberan solos cuando vencen.',
  UNIT_REPORT_SHARE_NOT_FOUND: 'Ese link ya no existe. Actualizamos la lista.',
  UNIT_REPORT_LINK_UNAVAILABLE: 'Este link ya no está disponible.',
  UNIT_REPORT_RATE_LIMITED: 'Hay demasiados pedidos seguidos. Probá de nuevo en un minuto.',
  INTERNAL_ERROR: META_GENERIC_ERROR_MESSAGE,
  SERVER_ERROR: META_GENERIC_ERROR_MESSAGE,
};

/**
 * ¿400 `VALIDATION_ERROR` con `field: "redirect_uri"` (redirect fuera de la lista del entorno)?
 *
 * @param {object} res Envelope.
 * @returns {boolean}
 */
export function isMetaRedirectUriRejected(res) {
  return res?._httpStatus === 400
    && readMetaErrorCode(res) === 'VALIDATION_ERROR'
    && String(res?.field || '').trim() === 'redirect_uri';
}

/**
 * Mensaje para un envelope con error. Decide por `code`; sin `code`, por el
 * status (el 401 del router no trae `code`) o el transporte (red caída →
 * `_httpStatus: null`).
 *
 * @param {object} res Envelope.
 * @param {string} [fallback]
 * @returns {string}
 */
export function getMetaErrorMessage(res, fallback = META_GENERIC_ERROR_MESSAGE) {
  const code = readMetaErrorCode(res);
  if (isMetaRedirectUriRejected(res)) return META_REDIRECT_URI_MESSAGE;
  if (code === 'VALIDATION_ERROR' && typeof res?.error === 'string' && res.error.trim()) {
    return res.error.trim();
  }
  if (code && META_ERROR_MESSAGES[code]) return META_ERROR_MESSAGES[code];
  if (res?._httpStatus === 401) return META_SESSION_EXPIRED_MESSAGE;
  if (res?._httpStatus == null) return META_NETWORK_MESSAGE;
  return fallback;
}

/** Aviso de `warnings` de `POST /connectmeta` por `code`. */
const META_WARNING_MESSAGES = {
  META_NO_AD_ACCOUNT: 'No compartiste ninguna cuenta publicitaria. Volvé a conectar y marcala en la ventana de Meta.',
};

/**
 * Mensajes de los `warnings` conocidos (los desconocidos se ignoran).
 *
 * @param {object} res Envelope de `POST /connectmeta`.
 * @returns {string[]}
 */
export function getMetaWarningMessages(res) {
  const list = Array.isArray(res?.warnings) ? res.warnings : [];
  return list
    .map((w) => META_WARNING_MESSAGES[String(w?.code || '').trim().toUpperCase()])
    .filter(Boolean);
}

/** Códigos que se resuelven volviendo a conectar (CTA «Reconectar», solo dueño). */
export const META_RECONNECT_ERROR_CODES = ['META_TOKEN_EXPIRED', 'META_PERMISSION_MISSING'];

/**
 * ¿El error pide reconectar?
 *
 * @param {object} res Envelope.
 * @returns {boolean}
 */
export const needsMetaReconnect = (res) => META_RECONNECT_ERROR_CODES.includes(readMetaErrorCode(res));

/**
 * Segundos de espera de un 429 (`retry_after_s` del body; si no, header
 * `Retry-After`). Sin dato → `fallback`.
 *
 * @param {object} res Envelope.
 * @param {number} [fallback]
 * @returns {number}
 */
export function readMetaRetryAfterSeconds(res, fallback = 60) {
  const fromBody = Number(res?.retry_after_s);
  if (Number.isFinite(fromBody) && fromBody > 0) return Math.ceil(fromBody);
  const header = typeof res?._httpHeaders?.get === 'function' ? Number(res._httpHeaders.get('retry-after')) : NaN;
  if (Number.isFinite(header) && header > 0) return Math.ceil(header);
  return fallback;
}

/**
 * Cuenta regresiva corta: `45 s`, `12:05`, `1 h 05 min`.
 *
 * @param {number} seconds
 * @returns {string}
 */
export function formatMetaCountdown(seconds) {
  const s = Math.max(0, Math.ceil(Number(seconds) || 0));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const h = Math.floor(s / 3600);
  return `${h} h ${String(Math.floor((s % 3600) / 60)).padStart(2, '0')} min`;
}

// Links con vencimiento del informe por propiedad (contrato: 30 días por defecto, 90 máximo, 10 activos).
export const UNIT_REPORT_SHARE_DEFAULT_DAYS = 30;
export const UNIT_REPORT_SHARE_MAX_DAYS = 90;
export const UNIT_REPORT_SHARE_MAX_ACTIVE = 10;

export const META_CSV_GENERIC_ERROR_MESSAGE = 'No pudimos generar el archivo. Probá de nuevo en un rato.';
/** 503 `META_ADS_NOT_MIGRATED`: la API de este entorno todavía no tiene la descarga. */
export const META_CSV_UNAVAILABLE_MESSAGE = 'La descarga todavía no está disponible en este entorno.';

const csvWaitText = (seconds) => (seconds < 60 ? `${seconds} segundos` : `${Math.ceil(seconds / 60)} minutos`);

/**
 * Mensaje de un error de la descarga CSV, siempre por `code` (el body del error es JSON aunque el
 * éxito sea `text/csv`). Sin `code` (404 `{"error":"Not Found"}`, 429 / 503 / 504 del Gateway con
 * `{message}`) o con un JSON ilegible → mensaje genérico; sin respuesta → «No se pudo hablar…».
 *
 * @param {object} res Envelope de `downloadMetaResultsCsv`.
 * @returns {string}
 */
export function getMetaCsvErrorMessage(res) {
  if (res?._httpStatus == null) return META_NETWORK_MESSAGE;
  const code = readMetaErrorCode(res);
  switch (code) {
    case 'VALIDATION_ERROR':
      return 'El pedido de descarga no es válido (período o tipo de archivo). Elegí otro período y probá de nuevo.';
    case 'AUTH_REQUIRED':
      return META_SESSION_EXPIRED_MESSAGE;
    case 'SPACE_FORBIDDEN':
      return META_ERROR_MESSAGES.SPACE_FORBIDDEN;
    case 'RECORD_NOT_FOUND':
      return 'No encontramos los datos que pediste: la unidad ya no existe o no es de este espacio.';
    case 'META_ADS_NOT_MIGRATED':
      return META_CSV_UNAVAILABLE_MESSAGE;
    case 'META_RATE_LIMITED': {
      const wait = Number(res?.retry_after_s);
      return Number.isFinite(wait) && wait > 0
        ? `Meta nos pidió esperar un momento. Probá de nuevo en ${csvWaitText(Math.ceil(wait))}.`
        : META_ERROR_MESSAGES.META_RATE_LIMITED;
    }
    case 'META_TOKEN_EXPIRED':
      return 'Se cortó la conexión con Meta. Reconectá Meta desde «Configuración de Meta» (si no sos el dueño del espacio, pedile que lo haga) y probá de nuevo.';
    default:
      if (!code && res._httpStatus === 401) return META_SESSION_EXPIRED_MESSAGE;
      return META_CSV_GENERIC_ERROR_MESSAGE;
  }
}
